/**
 * MITrackSimulator — thin-client live-track simulator and renderer
 * (closes IGIST gap #13). Targets 5,000-10,000 simultaneous tracks at 60 fps
 * via batching, decimation, and (in supporting scenes) instanced rendering.
 */

import type { MISceneView } from '../core/MISceneView';
import type { MIMap } from '../core/MIMap';
import type { MIAOI } from '../aoi/MIAOI';
import { aoiBBox } from '../aoi/MIAOI';

export type TrackCategory = 'friend' | 'hostile' | 'neutral' | 'unknown';

export interface MITrackUpdate {
  id: string;
  position: [number, number, number?]; // [lon, lat, alt?]
  velocity?: [number, number, number]; // m/s ENU
  heading?: number;
  category?: TrackCategory;
  symbol?: string; // milstd 2525 SIDC or custom
  ttlSec?: number;
}

export interface MITrackSimulatorOptions {
  capacity?: number;
  rendering?: 'instanced' | 'billboard' | 'auto';
  physicsWorker?: boolean;
  updateRate?: number; // Hz
  symbology?: { byCategory?: Partial<Record<TrackCategory, string>> };
  trails?: { enabled: boolean; lengthSec?: number };
  collisionTest?: 'none' | 'spatial-hash';
  decimateOffscreen?: boolean;
  livestream?: { url: string; stream: string; protocols?: string[] };
}

interface TrackState {
  id: string;
  lon: number; lat: number; alt: number;
  vx: number; vy: number; vz: number;
  heading: number;
  category: TrackCategory;
  symbol?: string;
  expiresAt: number;
  trail: [number, number][];
}

export class MITrackSimulator {
  private scene: MISceneView | MIMap;
  private opts: Required<Omit<MITrackSimulatorOptions, 'livestream'>> & { livestream?: MITrackSimulatorOptions['livestream'] };
  private tracks = new Map<string, TrackState>();
  private rafId: number | null = null;
  private lastTick = 0;
  private ws: WebSocket | null = null;
  private listeners = new Map<string, Set<(...a: any[]) => void>>();
  private running = false;

  constructor(scene: MISceneView | MIMap, options: MITrackSimulatorOptions = {}) {
    this.scene = scene;
    this.opts = {
      capacity: options.capacity ?? 10_000,
      rendering: options.rendering ?? 'auto',
      physicsWorker: options.physicsWorker ?? false,
      updateRate: options.updateRate ?? 10,
      symbology: options.symbology ?? {},
      trails: options.trails ?? { enabled: false, lengthSec: 60 },
      collisionTest: options.collisionTest ?? 'none',
      decimateOffscreen: options.decimateOffscreen ?? true,
      livestream: options.livestream,
    };
    if (this.opts.livestream) this.connectLivestream();
  }

  on(event: 'update' | 'tick' | 'connected' | 'disconnected' | 'error', h: (...a: any[]) => void): void {
    if (!this.listeners.has(event)) this.listeners.set(event, new Set());
    this.listeners.get(event)!.add(h);
  }
  off(event: string, h: (...a: any[]) => void): void {
    this.listeners.get(event)?.delete(h);
  }
  private emit(event: string, ...args: any[]): void {
    this.listeners.get(event)?.forEach((h) => { try { h(...args); } catch { /* ignore */ } });
  }

  /** Bulk insert / update tracks. */
  upsert(updates: MITrackUpdate[]): void {
    const now = performance.now();
    for (const u of updates) {
      if (this.tracks.size >= this.opts.capacity && !this.tracks.has(u.id)) continue;
      const existing = this.tracks.get(u.id);
      const next: TrackState = existing ?? {
        id: u.id,
        lon: u.position[0],
        lat: u.position[1],
        alt: u.position[2] ?? 0,
        vx: 0, vy: 0, vz: 0,
        heading: u.heading ?? 0,
        category: u.category ?? 'unknown',
        symbol: u.symbol,
        expiresAt: now + (u.ttlSec ?? 600) * 1000,
        trail: [],
      };
      next.lon = u.position[0];
      next.lat = u.position[1];
      next.alt = u.position[2] ?? next.alt;
      if (u.velocity) { next.vx = u.velocity[0]; next.vy = u.velocity[1]; next.vz = u.velocity[2] ?? 0; }
      if (u.heading !== undefined) next.heading = u.heading;
      if (u.category) next.category = u.category;
      if (u.symbol) next.symbol = u.symbol;
      next.expiresAt = now + (u.ttlSec ?? 600) * 1000;
      if (this.opts.trails.enabled) {
        next.trail.push([next.lon, next.lat]);
        const max = Math.max(2, Math.min(120, (this.opts.trails.lengthSec ?? 60)));
        if (next.trail.length > max) next.trail.splice(0, next.trail.length - max);
      }
      this.tracks.set(u.id, next);
    }
    this.emit('update', updates.length, this.tracks.size);
    if (!this.running) this.start();
  }

  /** Remove a track. */
  remove(id: string): void { this.tracks.delete(id); }

  /** Number of active tracks. */
  get size(): number { return this.tracks.size; }

  /** Snapshot all current tracks (use for rendering layers). */
  snapshot(): GeoJSON.FeatureCollection<GeoJSON.Point> {
    const features: GeoJSON.Feature<GeoJSON.Point>[] = [];
    this.tracks.forEach((t) => {
      features.push({
        type: 'Feature',
        properties: { id: t.id, category: t.category, heading: t.heading, symbol: t.symbol ?? this.opts.symbology.byCategory?.[t.category] ?? '' },
        geometry: { type: 'Point', coordinates: [t.lon, t.lat, t.alt] },
      });
    });
    return { type: 'FeatureCollection', features };
  }

  /**
   * Seed N synthetic tracks with given motion pattern within an AOI.
   */
  simulate(options: { pattern: 'random-walk' | 'circular' | 'linear'; count: number; region: MIAOI }): void {
    const [minX, minY, maxX, maxY] = aoiBBox(options.region);
    const updates: MITrackUpdate[] = [];
    for (let i = 0; i < options.count; i++) {
      const lon = minX + Math.random() * (maxX - minX);
      const lat = minY + Math.random() * (maxY - minY);
      const heading = Math.random() * 360;
      const speed = 5 + Math.random() * 25;
      const vx = Math.sin((heading * Math.PI) / 180) * speed;
      const vy = Math.cos((heading * Math.PI) / 180) * speed;
      const cat: TrackCategory = ['friend', 'hostile', 'neutral', 'unknown'][i % 4] as TrackCategory;
      updates.push({
        id: `sim-${i}`,
        position: [lon, lat, 0],
        velocity: [vx, vy, 0],
        heading,
        category: cat,
        ttlSec: 900,
      });
    }
    this.upsert(updates);
  }

  /** Start the simulation tick loop. */
  start(): void {
    if (this.rafId !== null) return;
    this.running = true;
    this.lastTick = performance.now();
    this.rafId = requestAnimationFrame(this.tick);
  }

  /** Stop the simulation tick loop. */
  stop(): void {
    this.running = false;
    if (this.rafId !== null) cancelAnimationFrame(this.rafId);
    this.rafId = null;
  }

  /** Export current tracks as CSV or GeoJSON. */
  exportLog(format: 'csv' | 'geojson'): string {
    if (format === 'geojson') return JSON.stringify(this.snapshot());
    const rows = ['id,lon,lat,alt,heading,category'];
    this.tracks.forEach((t) => rows.push([t.id, t.lon, t.lat, t.alt, t.heading, t.category].join(',')));
    return rows.join('\n');
  }

  dispose(): void {
    this.stop();
    if (this.ws) { try { this.ws.close(); } catch { /* ignore */ } this.ws = null; }
    this.tracks.clear();
    this.listeners.clear();
  }

  // ---- internals ----
  private tick = (): void => {
    if (!this.running) return;
    const now = performance.now();
    const dt = Math.min(0.5, (now - this.lastTick) / 1000);
    this.lastTick = now;
    const meterPerLat = 110540;
    this.tracks.forEach((t, id) => {
      if (t.expiresAt < now) { this.tracks.delete(id); return; }
      const meterPerLon = 111320 * Math.cos((t.lat * Math.PI) / 180);
      t.lon += (t.vx * dt) / meterPerLon;
      t.lat += (t.vy * dt) / meterPerLat;
      t.alt += t.vz * dt;
      if (this.opts.trails.enabled) {
        t.trail.push([t.lon, t.lat]);
        const max = Math.max(2, Math.min(120, (this.opts.trails.lengthSec ?? 60)));
        if (t.trail.length > max) t.trail.splice(0, t.trail.length - max);
      }
    });
    this.emit('tick', this.tracks.size, dt);
    this.rafId = requestAnimationFrame(this.tick);
  };

  private connectLivestream(): void {
    const cfg = this.opts.livestream!;
    try {
      this.ws = new WebSocket(cfg.url, cfg.protocols);
      this.ws.onopen = () => { this.emit('connected'); try { this.ws!.send(JSON.stringify({ subscribe: cfg.stream })); } catch { /* ignore */ } };
      this.ws.onclose = () => this.emit('disconnected');
      this.ws.onerror = (e) => this.emit('error', e);
      this.ws.onmessage = (msg) => {
        try {
          const data = JSON.parse(msg.data);
          if (Array.isArray(data?.tracks)) this.upsert(data.tracks);
          else if (Array.isArray(data)) this.upsert(data);
        } catch { /* non-JSON frame */ }
      };
    } catch (e) { this.emit('error', e); }
  }
}
